import { SdkAcdAgent } from '../sdk-acd/sdk-acd.service';
import { NiceSdkAuthenticationService } from '../sdk-auth/nice-sdk-authentication.service';
import { LoggerService } from 'src/app/core/services/logger.service';
import { Injectable } from '@angular/core';
import { EventHandlerService } from '../../services/event-handler.service';
import { CXonePopUrl } from '@nice-devone/common-sdk';
import { CxOneUtils } from '../util/CxOneUtils';
import {
  DigitalSessionDirection,
  DigitalSessionEndReason,
  DigitalSessionEventDTO,
  DigitalSessionEventType,
} from 'src/app/components/pessoa-fisica/domain/ContactCenter/DigitalSessionEventDTO';
import { SdkDigitalService } from '../sdk-digital/sdk-digital.service';
import {
  CXoneDigitalClient,
  CXoneDigitalContact,
} from '@nice-devone/digital-sdk';
import { Subject } from 'rxjs';
import { IContactCenterGerentesPF } from 'src/app/components/gerente-pf/interfaces/IContactCenterGerentesPF';
import {
  FocusEventDTO,
  FocusEventSource,
} from 'src/app/components/pessoa-fisica/domain/ContactCenter/FocusEventDTO';
import { getMediaType } from 'src/app/utils/cxone-utils';
import {
  ContactCenterMainService,
  TemplateValuesChannelProviders,
} from 'src/app/data/CXOne/services/main/contact-center-main.service';

@Injectable({
  providedIn: 'root',
})
export class ContactCenterGerentesPFService
  implements IContactCenterGerentesPF
{
  public onDigitalSessionEvent$ = new Subject<DigitalSessionEventDTO>();
  public onFocusEvent$ = new Subject<FocusEventDTO>();
  private pendingPopUrls = new Map<string, CXonePopUrl>();
  private channelProviderVariables: TemplateValuesChannelProviders | null =
    null;

  constructor(
    private niceSdkAuthService: NiceSdkAuthenticationService,
    private sdkAcdAgent: SdkAcdAgent,
    private logger: LoggerService,
    private eventHandler: EventHandlerService,
    private sdkDigital: SdkDigitalService,
    private mainService: ContactCenterMainService,
  ) {}

  public async initializer() {
    try {
      await this.sdkDigital.initializeEvents();
      CXoneDigitalClient.instance.digitalContactManager.onDigitalContactEvent.subscribe(
        async (digitalContact: CXoneDigitalContact) => {
          this.DigitalContactHandler(digitalContact);
        },
      );
      this.sdkAcdAgent.instance.screenPop.screenPopSubscribe(
        true,
        true,
        true,
        true,
        true,
        true,
      );
      this.sdkAcdAgent.instance.screenPop.popUrlSubject.subscribe(
        (popUrlData: CXonePopUrl) => {
          this.DigitalAttachDataEventHandler(popUrlData);
        },
      );
    } catch (error) {
      this.logger.warn('Falha inicialização de digital:', error);
    }
  }

  private async DigitalContactHandler(digitalContact: CXoneDigitalContact) {
    this.logger.info('Digital contact recebido: ', digitalContact);

    if (digitalContact.eventDetails.eventType === 'CaseInboxAssigned') {
      this.DigitalContactCaseAssignedHandler(digitalContact);
    }

    if (digitalContact.eventDetails.eventType === 'CaseInboxUnassigned') {
      this.DigitalContactCaseUnassignedHandler(digitalContact);
    }
  }

  private async DigitalContactCaseAssignedHandler(
    digitalContact: CXoneDigitalContact,
  ) {
    let attachData: any = {};

    const customFields =
      await this.mainService.generatePopUrlFronDigitalContact(
        digitalContact,
        'GERENTES-PF',
      );

    if (!customFields) {
      this.logger.warn(
        'GERENTES-PF - Dados não obtidos da extração do digitalEvent.',
      );

      const popUrl =
        this.pendingPopUrls.get(digitalContact.caseId) ??
        this.logger.warn(
          'GERENTES-PF - PopUrl não encontrado para o contato digital: ',
          digitalContact.caseId,
        );

      attachData = CxOneUtils.CreateDictionaryFromPopUrl(popUrl?.url) ?? {};
    } else {
      const popUrlJson = customFields.popUrl;
      const isChatPf =
        popUrlJson['u_json']['PONTODECONTATO'].includes('chat-gerente-pf');
      const channel = customFields.channel;

      if (channel === 'GERENTESPF' || channel === 'JURIDICO') {
        await this.GetChannelProvidersObject(channel);
      } else {
        if (isChatPf === true) {
          await this.GetChannelProvidersObject('GERENTESPF');
        } else {
          await this.GetChannelProvidersObject('JURIDICO');
        }
      }

      var popUrlData = await this.CreateObjectPopUrlToGerentesPF(
        customFields.popUrl,
      );

      this.logger.info(
        `Ataches Teste Gerentes-Pf: ${JSON.stringify(popUrlData)}`,
      );

      attachData = popUrlData;
    }

    const digitalSession: DigitalSessionEventDTO = {
      Id: digitalContact.caseId,
      AttachData: attachData,
      SessionDirection: DigitalSessionDirection.Inbound,
      SessionEventType: DigitalSessionEventType.Assigned,

      mediaType: getMediaType(digitalContact),
    };
    this.onDigitalSessionEvent$.next(digitalSession);
  }

  private DigitalContactCaseUnassignedHandler(
    digitalContact: CXoneDigitalContact,
  ) {
    let digitalSession: DigitalSessionEventDTO = {
      Id: digitalContact.caseId,
      SessionDirection: DigitalSessionDirection.Inbound,
      SessionEventType: DigitalSessionEventType.Unassigned,
      SessionEndReason: DigitalSessionEndReason.Closed,

      mediaType: getMediaType(digitalContact),
    };
    this.onDigitalSessionEvent$.next(digitalSession);
    return;
  }

  private DigitalAttachDataEventHandler(popUrl: CXonePopUrl) {
    this.logger.info('PopUrl recebido:', popUrl);

    if (popUrl.tabTitle?.toUpperCase() === 'ATTACHESDYNAMICS') {
      this.pendingPopUrls.set(popUrl.contactId, popUrl);
      this.logger.info(
        'Attachdata armazenado para contato digital: ',
        popUrl.contactId,
      );
    }

    if (popUrl.tabTitle?.toUpperCase() === 'FOCUS') {
      let focusEvent: FocusEventDTO = {
        Id: popUrl.contactId,
        Source: FocusEventSource.ContactCenter,
      };
      this.onFocusEvent$.next(focusEvent);
    }
  }

  private async GetChannelProvidersObject(channel: string): Promise<void> {
    await this.mainService
      .loadTemplateValues(channel)
      .then((obj) => {
        this.channelProviderVariables = obj;

        if (!obj) {
          this.logger.error(
            'GERESTES-PF - Channel provider nulo, ',
            JSON.stringify(obj, null, 2),
          );
        }

        this.logger.info(
          'GERESTES-PF - Channel provider variables object: ',
          JSON.stringify(obj, null, 2),
        );
      })
      .catch((error) => {
        this.logger.error(
          'GERESTES-PF - Erro ao carregar o object channel provider variables, ',
          error,
        );
      });
  }

  private CreateObjectPopUrlToGerentesPF(attachdata: any) {
    let popUrl: any = {};

    try {
      popUrl['ITAU_NAME'] = this.channelProviderVariables?.itau_name;
      popUrl['ITAU_U_NOMEPROJETO'] =
        this.channelProviderVariables?.itau_u_nomeprojeto;
      popUrl['ITAU_U_LK'] = this.channelProviderVariables?.itau_u_lk;
      popUrl['TEMPLATECREATESESSION'] =
        this.channelProviderVariables?.templateCreateSession;
      popUrl['TEMPLATEAUSENTE'] =
        this.channelProviderVariables?.templateAusente;
      popUrl['STATUSCODE'] = '1';
      popUrl['STATECODE'] = '0';
      popUrl['RETRIEVERECORDTABLE'] =
        this.channelProviderVariables?.retrieveRecordTable;
      popUrl['RETRIEVERECORDCOL'] =
        this.channelProviderVariables?.retrieveRecordCol;
      popUrl['FIELDNEGOCIO'] = this.channelProviderVariables?.fieldNegocio;
      popUrl['U_JSON'] = attachdata['u_json'];

      return popUrl;
    } catch (error) {}
  }
}
